import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'react-router';
import { InsuranceProvider } from '../routes/checklist/insurance-marketplace';
import { Button } from './Button';
import { InsuranceProviderCard } from './InsuranceProviderCard';
import { Spinner } from './Spinner';
import useLivlyUser from '@/context/UserProvider';
import { useIsInsuranceMarketplaceService } from '@/routes/insurance';

interface Props {
  isLoading: boolean;
  onSkip: () => void;
  insuranceProviders: InsuranceProvider[];
  onDone: () => Promise<void>;
}

function InsuranceMarketplace({
  isLoading,
  onSkip,
  insuranceProviders,
  onDone
}: Props) {
  const { user } = useLivlyUser();

  const isSkipUploadProof = insuranceProviders?.some(
    (provider) => provider?.skipUploadProof === true
  );
  const isMarketPlace = useIsInsuranceMarketplaceService(user);
  const isFromNativeApp = localStorage.getItem('mobile') === 'true';

  const shouldHideSkipButton = isFromNativeApp && isMarketPlace;

  return (
    <>
      <div className="flex justify-end">
        {!isSkipUploadProof ? (
          shouldHideSkipButton ? null : (
            <Button
              color="default"
              onClick={onSkip}
              className="flex items-center w-full gap-2 md:w-auto"
              disabled={isLoading}
            >
              {isLoading && <Spinner />}
              Skip
            </Button>
          )
        ) : (
          <Button
            color="default"
            onClick={onDone}
            className="flex items-center w-full gap-2 md:w-auto"
            disabled={isLoading}
          >
            {isLoading && <Spinner />}
            Next
          </Button>
        )}
      </div>
      <h4 className="mt-4">
        Your property requires proof of insurance prior to move in. You can
        browse our list of partners below and return to upload your policy
        document after purchasing.
      </h4>
      {!isSkipUploadProof && (
        <div className="mt-4">
          <Link
            to={`../insurance/upload-proof`}
            state={{ label: 'Insurance Marketplace', returnTo: -1 }}
            className="flex items-start gap-4 md:p-4 md:rounded-lg md:hover:bg-gray-50"
          >
            <FontAwesomeIcon
              icon={['far', 'arrow-up-to-line']}
              className="text-2xl text-red-400"
            />
            <div className="flex-1">
              <p className="font-medium">Upload proof</p>
              <p className="mt-1 text-sm">
                Provider proof of insurance. Once uploaded, a property manager
                will review.
              </p>
            </div>
            <FontAwesomeIcon icon="chevron-right" className="text-gray-400" />
          </Link>
        </div>
      )}
      <div className="pt-4 mt-4 border-t border-gray-200">
        {insuranceProviders.length === 0 && (
          <div className="py-10">
            <p>Sorry. There are currently no insurance providers available.</p>
          </div>
        )}
        {insuranceProviders.length > 0 && (
          <div className="flex flex-wrap gap-4">
            {insuranceProviders.map((provider) => (
              <InsuranceProviderCard key={provider.id} provider={provider} />
            ))}
          </div>
        )}
      </div>
    </>
  );
}

export { InsuranceMarketplace };
